import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isSupabaseConfigured, supabase } from '../lib/supabaseClient';
import {
  createPup,
  deletePup,
  fetchPups,
  pupsStoreEnabled,
  removePupImages,
  updatePup,
  uploadPupImages
} from '../services/pupsStore';
import {
  createGalleryAlbum,
  deleteGalleryAlbum,
  fetchGalleryAlbums,
  galleryStoreEnabled,
  updateGalleryAlbum,
  uploadGalleryImages
} from '../services/galleryStore';
import { FEMALE_PRICE, MALE_PRICE } from '../data/cataloguePets';

const ADMIN_EMAIL_ALLOWLIST = (process.env.REACT_APP_ADMIN_EMAIL || '').trim().toLowerCase();
const MAX_IMAGE_MB = 6;
const MAX_IMAGE_BYTES = MAX_IMAGE_MB * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp';

function formatMoney(value) {
  const n = typeof value === 'number' ? value : value ? Number(value) : null;
  if (!Number.isFinite(n)) return '';
  return String(n);
}

function validateImageFiles(files, { allowEmpty = false } = {}) {
  const list = Array.from(files || []);
  if (!list.length && allowEmpty) return { ok: true, files: [] };
  if (!list.length) return { ok: false, files: [], message: 'Please choose at least one image.' };

  for (const f of list) {
    if (!ALLOWED_IMAGE_TYPES.has(String(f.type || '').toLowerCase())) {
      return { ok: false, files: [], message: 'Only JPG, PNG, or WEBP images are allowed.' };
    }
    if (typeof f.size === 'number' && f.size > MAX_IMAGE_BYTES) {
      return { ok: false, files: [], message: `Each image must be ${MAX_IMAGE_MB}MB or smaller.` };
    }
  }

  return { ok: true, files: list };
}

export default function AdminPage({ brand, logo, defaultPets }) {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [email, setEmail] = useState(ADMIN_EMAIL_ALLOWLIST || '');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [isAuthBusy, setIsAuthBusy] = useState(false);

  const [pups, setPups] = useState([]);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');

  const [albums, setAlbums] = useState([]);
  const [albumsError, setAlbumsError] = useState('');

  const isReady = pupsStoreEnabled();
  const isGalleryReady = galleryStoreEnabled();

  useEffect(() => {
    if (!supabase) return undefined;
    supabase.auth.getSession().then(({ data }) => setSession(data?.session || null));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
    });
    return () => sub?.subscription?.unsubscribe?.();
  }, []);

  const isAllowedEmail = useMemo(() => {
    if (!ADMIN_EMAIL_ALLOWLIST) return true;
    return String(email || '').trim().toLowerCase() === ADMIN_EMAIL_ALLOWLIST;
  }, [email]);

  const load = useCallback(async () => {
    if (!isReady) return;
    setIsBusy(true);
    setError('');
    try {
      const list = await fetchPups();
      setPups(list);
    } catch (e) {
      setError(e?.message || 'Failed to load pups.');
    } finally {
      setIsBusy(false);
    }
  }, [isReady]);

  const loadAlbums = useCallback(async () => {
    if (!isGalleryReady) return;
    setAlbumsError('');
    try {
      const list = await fetchGalleryAlbums();
      setAlbums(list);
    } catch (e) {
      setAlbumsError(e?.message || 'Failed to load gallery albums.');
    }
  }, [isGalleryReady]);

  useEffect(() => {
    if (!session) return;
    load();
    loadAlbums();
  }, [session, load, loadAlbums]);

  const signIn = async (e) => {
    e.preventDefault();
    if (!supabase) return;
    setAuthError('');
    if (!isAllowedEmail) {
      setAuthError('This email is not allowed for this admin portal.');
      return;
    }
    setIsAuthBusy(true);
    try {
      const { error: err } = await supabase.auth.signInWithPassword({ email, password });
      if (err) throw err;
      setPassword('');
    } catch (err) {
      setAuthError(err?.message || 'Login failed.');
    } finally {
      setIsAuthBusy(false);
    }
  };

  const signOut = async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
  };

  const onCreate = async (draft, files) => {
    setIsBusy(true);
    setError('');
    try {
      const created = await createPup(draft);
      const validated = validateImageFiles(files, { allowEmpty: false });
      if (!validated.ok) {
        await deletePup(created.id, []);
        throw new Error(validated.message);
      }

      try {
        const paths = await uploadPupImages(created.id, validated.files);
        const updated = await updatePup(created.id, { image_paths: paths });
        setPups((prev) => [updated, ...prev]);
      } catch (err) {
        await deletePup(created.id, []);
        throw err;
      }
    } catch (e) {
      setError(e?.message || 'Failed to create pup.');
    } finally {
      setIsBusy(false);
    }
  };

  const onUpdate = async (id, patch) => {
    setError('');
    try {
      const updated = await updatePup(id, patch);
      setPups((prev) => prev.map((p) => (p.id === id ? updated : p)));
    } catch (e) {
      setError(e?.message || 'Failed to save changes.');
    }
  };

  const onDelete = async (id, imagePaths) => {
    setIsBusy(true);
    setError('');
    try {
      await deletePup(id, imagePaths);
      setPups((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      setError(e?.message || 'Failed to delete pup.');
    } finally {
      setIsBusy(false);
    }
  };

  const onAddImages = async (id, currentPaths, files) => {
    setIsBusy(true);
    setError('');
    try {
      const validated = validateImageFiles(files, { allowEmpty: false });
      if (!validated.ok) throw new Error(validated.message);
      const newPaths = await uploadPupImages(id, validated.files);
      const nextPaths = [...(currentPaths || []), ...newPaths];
      await onUpdate(id, { image_paths: nextPaths });
    } catch (e) {
      setError(e?.message || 'Failed to upload images.');
    } finally {
      setIsBusy(false);
    }
  };

  const onRemovePupImage = async (id, currentPaths, idx) => {
    const list = Array.from(currentPaths || []);
    const path = list[idx];
    if (!path) return;
    // eslint-disable-next-line no-alert
    const ok = window.confirm('Remove this photo?');
    if (!ok) return;

    setIsBusy(true);
    setError('');
    try {
      await removePupImages([path]);
      const nextPaths = list.filter((_, i) => i !== idx);
      await onUpdate(id, { image_paths: nextPaths });
    } catch (e) {
      setError(e?.message || 'Failed to remove photo.');
    } finally {
      setIsBusy(false);
    }
  };

  const bulkMarkNotAvailable = async () => {
    if (!pups.length) return;
    // eslint-disable-next-line no-alert
    const ok = window.confirm('Mark ALL pups as Not available?');
    if (!ok) return;
    setIsBusy(true);
    setError('');
    try {
      for (const p of pups) {
        // eslint-disable-next-line no-await-in-loop
        await updatePup(p.id, { status: 'Not available' });
      }
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to update pups.');
    } finally {
      setIsBusy(false);
    }
  };

  const deleteAvailablePups = async () => {
    const available = pups.filter((p) => {
      const s = String(p.status || '').trim().toLowerCase();
      return !(s === 'sold' || s === 'not available' || s === 'unavailable' || s === 'out of stock');
    });
    if (!available.length) return;
    // eslint-disable-next-line no-alert
    const ok = window.confirm(`Delete ${available.length} available pup(s)? This cannot be undone.`);
    if (!ok) return;
    setIsBusy(true);
    setError('');
    try {
      for (const p of available) {
        // eslint-disable-next-line no-await-in-loop
        await deletePup(p.id, p.image_paths || []);
      }
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to delete pups.');
    } finally {
      setIsBusy(false);
    }
  };

  const onCreateAlbum = async (draft) => {
    setIsBusy(true);
    setAlbumsError('');
    try {
      const created = await createGalleryAlbum(draft);
      setAlbums((prev) => [created, ...prev]);
    } catch (e) {
      setAlbumsError(e?.message || 'Failed to create album.');
    } finally {
      setIsBusy(false);
    }
  };

  const onUpdateAlbum = async (id, patch) => {
    setAlbumsError('');
    try {
      const updated = await updateGalleryAlbum(id, patch);
      setAlbums((prev) => prev.map((a) => (a.id === id ? updated : a)));
    } catch (e) {
      setAlbumsError(e?.message || 'Failed to update album.');
    }
  };

  const onDeleteAlbum = async (id, imagePaths) => {
    setIsBusy(true);
    setAlbumsError('');
    try {
      await deleteGalleryAlbum(id, imagePaths);
      setAlbums((prev) => prev.filter((a) => a.id !== id));
    } catch (e) {
      setAlbumsError(e?.message || 'Failed to delete album.');
    } finally {
      setIsBusy(false);
    }
  };

  const onAddAlbumImages = async (id, currentPaths, files) => {
    setIsBusy(true);
    setAlbumsError('');
    try {
      const validated = validateImageFiles(files, { allowEmpty: false });
      if (!validated.ok) throw new Error(validated.message);
      const newPaths = await uploadGalleryImages(id, validated.files);
      const nextPaths = [...(currentPaths || []), ...newPaths];
      await onUpdateAlbum(id, { image_paths: nextPaths });
    } catch (e) {
      setAlbumsError(e?.message || 'Failed to upload album images.');
    } finally {
      setIsBusy(false);
    }
  };

  const seedDefaults = async () => {
    if (!defaultPets?.length) return;
    setIsBusy(true);
    setError('');
    try {
      for (const p of defaultPets) {
        // eslint-disable-next-line no-await-in-loop
        await createPup({
          id: p.id,
          title: p.title,
          sex: p.sex,
          breed: p.breed,
          status: p.status,
          price: p.price,
          age: p.age,
          weight: p.weight,
          size: p.size,
          description: p.description,
          image_paths: []
        });
      }
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to seed defaults.');
    } finally {
      setIsBusy(false);
    }
  };

  if (!isSupabaseConfigured) {
    return (
      <div className="page admin-page">
        <div className="admin-shell">
          <div className="admin-card">
            <div className="admin-title">Admin portal setup needed</div>
            <p className="admin-help">
              This admin portal requires Supabase (database + storage). Add `REACT_APP_SUPABASE_URL` and
              `REACT_APP_SUPABASE_ANON_KEY` in Vercel (and locally), then redeploy.
            </p>
            <button className="admin-link" type="button" onClick={() => navigate('/')}>
              Back to site
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="page admin-page">
        <div className="admin-shell">
          <div className="admin-card">
            <div className="admin-brand">
              <img className="admin-logo" src={logo} alt="" />
              <div>
                <div className="admin-title">{brand} Admin</div>
                <div className="admin-subtitle">Sign in to manage the catalogue</div>
              </div>
            </div>

            <form className="admin-form" onSubmit={signIn}>
              <label className="admin-field">
                <span>Email</span>
                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="username" />
              </label>
              <label className="admin-field">
                <span>Password</span>
                <input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  autoComplete="current-password"
                />
              </label>
              {authError ? <div className="admin-error">{authError}</div> : null}
              <button className="admin-primary" type="submit" disabled={isAuthBusy}>
                {isAuthBusy ? 'Signing in…' : 'Sign in'}
              </button>
              <button className="admin-link" type="button" onClick={() => navigate('/')}>
                Back to site
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page admin-page">
      <div className="admin-shell">
        <header className="admin-header">
          <div className="admin-brand">
            <img className="admin-logo" src={logo} alt="" />
            <div>
              <div className="admin-title">{brand} Admin</div>
              <div className="admin-subtitle">Add pups, edit details, upload photos</div>
            </div>
          </div>
          <div className="admin-actions">
            <button className="admin-link" type="button" onClick={() => navigate('/')}>
              Back to site
            </button>
            <button className="admin-link" type="button" onClick={signOut}>
              Sign out
            </button>
          </div>
        </header>

        <main className="admin-main">
          <section className="admin-panel">
            <div className="admin-panel-head">
              <div className="admin-panel-title">Add new pup</div>
              <div className="admin-panel-sub">Female defaults to ${FEMALE_PRICE} • Male defaults to ${MALE_PRICE}</div>
            </div>
            <AdminCreateForm onCreate={onCreate} isBusy={isBusy} />
          </section>

          <section className="admin-panel">
            <div className="admin-panel-head">
              <div className="admin-panel-title">Catalogue pups</div>
              <div className="admin-panel-sub">{pups.length} pups</div>
            </div>
            {error ? <div className="admin-error">{error}</div> : null}
            {isBusy ? <div className="admin-loading">Working…</div> : null}

            <div className="admin-list">
              {pups.map((p) => (
                <AdminPupRow
                  key={p.id}
                  pup={p}
                  onUpdate={onUpdate}
                  onDelete={onDelete}
                  onAddImages={onAddImages}
                  onRemoveImage={onRemovePupImage}
                />
              ))}
            </div>

            <div className="admin-footer">
              <button className="admin-secondary" type="button" onClick={load} disabled={isBusy}>
                Refresh list
              </button>
              <button className="admin-secondary" type="button" onClick={seedDefaults} disabled={isBusy}>
                Seed defaults (optional)
              </button>
              <button className="admin-secondary" type="button" onClick={bulkMarkNotAvailable} disabled={isBusy}>
                Mark all Not available
              </button>
              <button className="admin-danger" type="button" onClick={deleteAvailablePups} disabled={isBusy}>
                Delete all Available
              </button>
            </div>
          </section>

          <section className="admin-panel">
            <div className="admin-panel-head">
              <div className="admin-panel-title">Gallery</div>
              <div className="admin-panel-sub">{albums.length} albums</div>
            </div>

            {!isGalleryReady ? (
              <div className="admin-help">
                Gallery admin requires a Supabase bucket named <strong>gallery</strong> (public) and the
                <strong> gallery_albums</strong> table. Re-run <code>supabase-setup.sql</code> and create the bucket.
              </div>
            ) : (
              <>
                {albumsError ? <div className="admin-error">{albumsError}</div> : null}
                <AdminAlbumCreateForm onCreate={onCreateAlbum} isBusy={isBusy} />
                <div className="admin-list">
                  {albums.map((a) => (
                    <AdminAlbumRow
                      key={a.id}
                      album={a}
                      onUpdate={onUpdateAlbum}
                      onDelete={onDeleteAlbum}
                      onAddImages={onAddAlbumImages}
                    />
                  ))}
                </div>
                <div className="admin-footer">
                  <button className="admin-secondary" type="button" onClick={loadAlbums} disabled={isBusy}>
                    Refresh gallery
                  </button>
                </div>
              </>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}

function AdminCreateForm({ onCreate, isBusy }) {
  const [title, setTitle] = useState('');
  const [sex, setSex] = useState('Female');
  const [breed, setBreed] = useState('Teacup Chihuahua');
  const [status, setStatus] = useState('Available');
  const [price, setPrice] = useState(String(FEMALE_PRICE));
  const [age, setAge] = useState('');
  const [weight, setWeight] = useState('');
  const [size, setSize] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState(null);
  const [fileError, setFileError] = useState('');

  useEffect(() => {
    setPrice(String(String(sex).toLowerCase() === 'male' ? MALE_PRICE : FEMALE_PRICE));
  }, [sex]);

  const submit = async (e) => {
    e.preventDefault();
    const id = crypto.randomUUID();
    const validated = validateImageFiles(files, { allowEmpty: false });
    if (!validated.ok) {
      setFileError(validated.message);
      return;
    }
    setFileError('');
    const draft = {
      id,
      title: title.trim() || 'New pup',
      sex,
      breed: breed.trim(),
      status,
      price: Number(price),
      age: age.trim() || 'Ask for details',
      weight: weight.trim() || 'Ask for details',
      size: size.trim() || 'Ask for details',
      description: description.trim() || 'Message us for more details.',
      image_paths: []
    };

    await onCreate(draft, validated.files);
    setTitle('');
    setAge('');
    setWeight('');
    setSize('');
    setDescription('');
    setFiles(null);
    e.target.reset?.();
  };

  return (
    <form className="admin-form-grid" onSubmit={submit}>
      <label className="admin-field">
        <span>Name</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Bella" />
      </label>
      <label className="admin-field">
        <span>Sex</span>
        <select value={sex} onChange={(e) => setSex(e.target.value)}>
          <option>Female</option>
          <option>Male</option>
        </select>
      </label>
      <label className="admin-field">
        <span>Status</span>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option>Available</option>
          <option>Not available</option>
          <option>Sold</option>
        </select>
      </label>
      <label className="admin-field">
        <span>Adoption fee</span>
        <input value={price} onChange={(e) => setPrice(e.target.value)} inputMode="numeric" />
      </label>
      <label className="admin-field admin-field-wide">
        <span>Breed</span>
        <input value={breed} onChange={(e) => setBreed(e.target.value)} />
      </label>
      <label className="admin-field">
        <span>Age</span>
        <input value={age} onChange={(e) => setAge(e.target.value)} placeholder="e.g. 8 weeks" />
      </label>
      <label className="admin-field">
        <span>Weight</span>
        <input value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="e.g. 2.3 lbs" />
      </label>
      <label className="admin-field">
        <span>Size</span>
        <input value={size} onChange={(e) => setSize(e.target.value)} placeholder="e.g. Small" />
      </label>
      <label className="admin-field admin-field-wide">
        <span>Short description</span>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Friendly, playful, good with kids…"
          rows={3}
        />
      </label>
      <label className="admin-field admin-field-wide">
        <span>Photos (JPG/PNG/WEBP • up to {MAX_IMAGE_MB}MB each)</span>
        <input
          type="file"
          accept={IMAGE_ACCEPT}
          multiple
          onChange={(e) => {
            setFiles(e.target.files);
            const v = validateImageFiles(e.target.files, { allowEmpty: true });
            setFileError(v.ok ? '' : v.message);
          }}
        />
      </label>
      {fileError ? <div className="admin-error admin-field-wide">{fileError}</div> : null}
      <button className="admin-primary admin-field-wide" type="submit" disabled={isBusy}>
        {isBusy ? 'Saving…' : 'Create pup'}
      </button>
      <div className="admin-help admin-field-wide">
        Tip: use clear, well-lit photos. Keep files small for fast loading on mobile.
      </div>
    </form>
  );
}

function AdminPupRow({ pup, onUpdate, onDelete, onAddImages, onRemoveImage }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => ({
    title: pup.title,
    sex: pup.sex,
    breed: pup.breed,
    status: pup.status,
    price: formatMoney(pup.price),
    age: pup.age,
    weight: pup.weight,
    size: pup.size,
    description: pup.description
  }));
  const [imagePaths, setImagePaths] = useState([]);

  useEffect(() => {
    setDraft({
      title: pup.title,
      sex: pup.sex,
      breed: pup.breed,
      status: pup.status,
      price: formatMoney(pup.price),
      age: pup.age,
      weight: pup.weight,
      size: pup.size,
      description: pup.description
    });
    setImagePaths(Array.isArray(pup.image_paths) ? pup.image_paths : []);
  }, [pup]);

  const save = async () => {
    await onUpdate(pup.id, { ...draft, price: draft.price ? Number(draft.price) : null });
  };

  return (
    <div className="admin-row">
      <button className="admin-row-top" type="button" onClick={() => setOpen((v) => !v)}>
        <div className="admin-row-left">
          <div className="admin-thumb">{pup.images?.[0] ? <img src={pup.images[0]} alt="" /> : <div className="admin-thumb-empty" />}</div>
          <div className="admin-row-meta">
            <div className="admin-row-title">{pup.title}</div>
            <div className="admin-row-sub">
              {pup.status} • {pup.sex || '—'} • {typeof pup.price === 'number' ? `$${pup.price}` : 'Ask fee'}
            </div>
          </div>
        </div>
        <div className="admin-row-right">{open ? 'Hide' : 'Edit'}</div>
      </button>

      {open ? (
        <div className="admin-row-body">
          <div className="admin-form-grid">
            <label className="admin-field">
              <span>Name</span>
              <input value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} />
            </label>
            <label className="admin-field">
              <span>Sex</span>
              <select value={draft.sex} onChange={(e) => setDraft((d) => ({ ...d, sex: e.target.value }))}>
                <option>Female</option>
                <option>Male</option>
              </select>
            </label>
            <label className="admin-field">
              <span>Status</span>
              <select value={draft.status} onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}>
                <option>Available</option>
                <option>Not available</option>
                <option>Sold</option>
              </select>
            </label>
            <label className="admin-field">
              <span>Adoption fee</span>
              <input value={draft.price} onChange={(e) => setDraft((d) => ({ ...d, price: e.target.value }))} />
            </label>
            <label className="admin-field admin-field-wide">
              <span>Breed</span>
              <input value={draft.breed} onChange={(e) => setDraft((d) => ({ ...d, breed: e.target.value }))} />
            </label>
            <label className="admin-field">
              <span>Age</span>
              <input value={draft.age} onChange={(e) => setDraft((d) => ({ ...d, age: e.target.value }))} />
            </label>
            <label className="admin-field">
              <span>Weight</span>
              <input value={draft.weight} onChange={(e) => setDraft((d) => ({ ...d, weight: e.target.value }))} />
            </label>
            <label className="admin-field">
              <span>Size</span>
              <input value={draft.size} onChange={(e) => setDraft((d) => ({ ...d, size: e.target.value }))} />
            </label>
            <label className="admin-field admin-field-wide">
              <span>Short description</span>
              <textarea
                value={draft.description}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                rows={3}
              />
            </label>
          </div>

          <div className="admin-media">
            <div className="admin-media-title">Photos</div>
            <div className="admin-media-grid">
              {(pup.images || []).map((src, idx) => (
                <div className="admin-media-item" key={src}>
                  <img src={src} alt="" loading="lazy" />
                  {imagePaths.length ? (
                    <button
                      className="admin-media-remove"
                      type="button"
                      onClick={() => onRemoveImage(pup.id, imagePaths, idx)}
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
            <input
              className="admin-file"
              type="file"
              accept={IMAGE_ACCEPT}
              multiple
              onChange={(e) => onAddImages(pup.id, imagePaths, e.target.files)}
            />
            <div className="admin-help">
              Tip: make the storage bucket public so website visitors can see photos. For deletion, use the Supabase
              Storage screen if needed.
            </div>
          </div>

          <div className="admin-row-actions">
            <button className="admin-secondary" type="button" onClick={save}>
              Save changes
            </button>
            <button className="admin-danger" type="button" onClick={() => onDelete(pup.id, imagePaths)}>
              Delete pup
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AdminAlbumCreateForm({ onCreate, isBusy }) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    await onCreate({
      id: crypto.randomUUID(),
      title: title.trim() || 'Gallery',
      category: category.trim(),
      description: description.trim(),
      image_paths: []
    });
    setTitle('');
    setCategory('');
    setDescription('');
    e.target.reset?.();
  };

  return (
    <form className="admin-form-grid" onSubmit={submit}>
      <label className="admin-field admin-field-wide">
        <span>Album title</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. New Litter" />
      </label>
      <label className="admin-field">
        <span>Category</span>
        <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Teacup Chihuahua" />
      </label>
      <label className="admin-field admin-field-wide">
        <span>Description (optional)</span>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Short caption for this album…"
          rows={3}
        />
      </label>
      <button className="admin-primary admin-field-wide" type="submit" disabled={isBusy}>
        {isBusy ? 'Saving…' : 'Create album'}
      </button>
    </form>
  );
}

function AdminAlbumRow({ album, onUpdate, onDelete, onAddImages }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => ({
    title: album.title || '',
    category: album.category || '',
    description: album.description || ''
  }));
  const [imagePaths, setImagePaths] = useState([]);

  useEffect(() => {
    setDraft({
      title: album.title || '',
      category: album.category || '',
      description: album.description || ''
    });
    setImagePaths(Array.isArray(album.image_paths) ? album.image_paths : []);
  }, [album]);

  const save = async () => {
    await onUpdate(album.id, { ...draft });
  };

  return (
    <div className="admin-row">
      <button className="admin-row-top" type="button" onClick={() => setOpen((v) => !v)}>
        <div className="admin-row-left">
          <div className="admin-thumb">
            {album.images?.[0] ? <img src={album.images[0]} alt="" /> : <div className="admin-thumb-empty" />}
          </div>
          <div className="admin-row-meta">
            <div className="admin-row-title">{album.title}</div>
            <div className="admin-row-sub">{album.category || 'Gallery'} • {(album.images || []).length} photos</div>
          </div>
        </div>
        <div className="admin-row-right">{open ? 'Hide' : 'Edit'}</div>
      </button>

      {open ? (
        <div className="admin-row-body">
          <div className="admin-form-grid">
            <label className="admin-field admin-field-wide">
              <span>Title</span>
              <input value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} />
            </label>
            <label className="admin-field">
              <span>Category</span>
              <input value={draft.category} onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))} />
            </label>
            <label className="admin-field admin-field-wide">
              <span>Description</span>
              <textarea
                value={draft.description}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                rows={3}
              />
            </label>
          </div>

          <div className="admin-media">
            <div className="admin-media-title">Photos</div>
            <div className="admin-media-grid">
              {(album.images || []).map((src) => (
                <div className="admin-media-item" key={src}>
                  <img src={src} alt="" loading="lazy" />
                </div>
              ))}
            </div>
            <input
              className="admin-file"
              type="file"
              accept={IMAGE_ACCEPT}
              multiple
              onChange={(e) => onAddImages(album.id, imagePaths, e.target.files)}
            />
            <div className="admin-help">Upload multiple photos to group them into a single album.</div>
          </div>

          <div className="admin-row-actions">
            <button className="admin-secondary" type="button" onClick={save}>
              Save changes
            </button>
            <button className="admin-danger" type="button" onClick={() => onDelete(album.id, imagePaths)}>
              Delete album
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
